import { createSlice } from "@reduxjs/toolkit";

import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * One troubleshooting-agent run the user is still owed a toast for. Everything
 * here is what the toasts need to say and where they need to link — the run's
 * own data lives on the activity.
 */
export interface TroubleshootingRun {
  /** The AGENT's activity (the task run), not the failure it diagnoses. */
  activityId: string;
  /**
   * The FAILED activity this run diagnoses — the `ACTIVITY_ID` it was handed.
   *
   * The reverse handle. The toasts key off the agent's activity, but the CTA
   * surfaces only ever hold the failure's id, and they need to ask "is a run
   * already in flight for this one?" before offering to start another. The API
   * accepts a duplicate run, so nothing below the UI prevents paying twice.
   */
  sourceActivityId: string;
  organizationId: string;
  projectId: string;
  environmentId: string;
  /** The environment's display title, for the completion toast's body. */
  environmentName: string;
}

export interface TroubleshootingRunsState {
  byActivityId: Record<string, TroubleshootingRun>;
}

const initialState: TroubleshootingRunsState = { byActivityId: {} };

/**
 * Session state: "toasts I still owe this user", keyed by the agent activity.
 *
 * Deliberately its own slice rather than a field on `environmentTasks`. That
 * slice is server state the project websocket owns and refetches wholesale; a
 * pending toast has a different lifetime — it outlives the page that started the
 * run, and it must survive a task refetch. Nothing here is ever fetched.
 *
 * A run enters on `runStarted` (the run POST succeeded) and leaves on one of two
 * events: `runReported`, once its report parsed and the completion toast fired,
 * or `runEndedWithoutReport`, once its activity settled with no report to show.
 * Both mean the same thing to this slice — the run has nothing further to say.
 *
 * That same span — POST accepted until one of those lands — is what the CTA
 * surfaces read through `sourceActivityId` to show "Troubleshooting..." instead
 * of offering a second run. So the second exit is load-bearing, not tidiness: a
 * cancelled run produces no report, and without it the failure it diagnoses
 * would keep its CTA suppressed for the rest of the session, leaving the user
 * unable to start another run after cancelling one.
 *
 * Two cases still outlive their run: an activity that never reaches the store is
 * never streamed, and one whose state never settles is never proven finished.
 * Each leaves one small object behind for the session, which is cheaper than a
 * timer that could fire a "gave up" toast for a run that was merely slow.
 */
const troubleshootingRuns = createSlice({
  name: "troubleshootingRuns",
  initialState,
  reducers: {
    runStarted(state, { payload }: PayloadAction<TroubleshootingRun>) {
      state.byActivityId[payload.activityId] = payload;
    },
    runReported(state, { payload }: PayloadAction<string>) {
      delete state.byActivityId[payload];
    },
    /**
     * The run's activity reached a terminal state with no report in its log — it
     * was cancelled, or the agent died before printing one. Same effect as
     * `runReported`, kept as its own action because no toast is owed for it and
     * the two are worth telling apart in a trace.
     */
    runEndedWithoutReport(state, { payload }: PayloadAction<string>) {
      delete state.byActivityId[payload];
    }
  }
});

export const { runStarted, runReported, runEndedWithoutReport } =
  troubleshootingRuns.actions;

export default troubleshootingRuns.reducer;
